/** The Pre-check-in tab's binding of the records core (backend plan, P4a): what FieldSlot and
 *  the Ledger need to know about an operator's pre-check-in settings that the catalog alone
 *  does not say.
 *
 *  - Editability: `console_top_manage`, else read-only with the reason.
 *  - Ledger: one group ("Pre-check-in settings"); lists read as counts ("4 fields · 1
 *    question"), their changes in words under the row (the local plan's notes). A required
 *    description — the PUT's reason, 3–500 characters — at the ledger's foot.
 *  - A scope's fields, its questions' switch and categories move together, and the party's
 *    follow the lead booker's while "Same fields for party members" is on (paired reverts,
 *    as on the tour).
 *  - Warnings on a change: a relabelled field reaches the operator's tours (a custom field's
 *    with the count of tours that ask it); party members' information switched off keeps a
 *    party at Partial.
 *  - Preview: the local plan (`useOperatorLocalPlan`); Save confirms a custom field relabelled
 *    or retyped on tours. Apply: `useOperatorPrecheckApply`. */
import { useMemo, useRef } from 'react';

import { describeFields, PRECHECK, sharedFieldWarnings, splitFields } from '@/features/precheck/model';
import type { DraftState, RecordKey } from '@/features/records/draft/changeSet';
import type { RecordEditorConfig, RecordField, RecordWidget } from '@/features/records/editor/types';

import type { OperatorPreCheckBaseline } from './baselines';
import { OPERATOR_MANAGE_PERM, OPERATOR_PRECHECK_FIELDS } from './catalog';
import { CONFIRM_FOLLOW_UPS, useOperatorLocalPlan } from './localPlan';
import {
  asDefinitions,
  asWaiverRows,
  DEFINITIONS_PATH,
  fieldListSummary,
  isCustomRow,
  LIST_PATHS,
  REASON_MAX,
  REASON_MIN,
  typeLabel,
  WAIVERS_PATH,
} from './model';
import { useOperatorPrecheckApply } from './useOperatorPrecheckApply';

const LEAD_TRIO = [PRECHECK('lead_booker_fields'), PRECHECK('lead_tag_categories_enabled'), PRECHECK('lead_selected_tag_categories')];
const PARTY_TRIO = [PRECHECK('party_member_fields'), PRECHECK('party_tag_categories_enabled'), PRECHECK('party_selected_tag_categories')];
const APPLY_SAME = PRECHECK('apply_same_fields_to_party_members');
const LINKED = new Set([...LEAD_TRIO, ...PARTY_TRIO, APPLY_SAME]);
const SELECTED_PATHS = new Set([PRECHECK('lead_selected_tag_categories'), PRECHECK('party_selected_tag_categories')]);
export const COLLECT_PARTY_PATH = PRECHECK('collect_party_members_info');

/** Said wherever "Collect party members' information" is off (plan F10 / D4). */
export const PARTY_NOT_COLLECTED =
  'With this off, a booking of more than one guest stays at Partial — it never reaches Completed';

export interface OperatorPrecheckConfigInput {
  baseline: OperatorPreCheckBaseline;
  /** console_top_manage. */
  canEdit: boolean;
  operatorName: string;
}

const tours = (n: number) => `${n} ${n === 1 ? 'tour' : 'tours'}`;

/** The pure half of the config (everything but the hooks and widgets), for tests. */
export function operatorPrecheckConfigParts({
  baseline,
  canEdit,
  operatorName,
}: OperatorPrecheckConfigInput): Omit<RecordEditorConfig, 'usePreview' | 'useApply' | 'widgets'> {
  const rec = baseline.rec;
  const stored = baseline.paths[rec] ?? {};
  const { options } = baseline.data;
  const categoryName = (code: string) => options.categories.find((c) => c.code === code)?.name ?? code;

  const formatValue = (field: RecordField, _rec: RecordKey, value: unknown): string | undefined => {
    if ((LIST_PATHS as readonly string[]).includes(field.key)) return describeFields(value);
    if (SELECTED_PATHS.has(field.key)) {
      const codes = Array.isArray(value) ? value.map(String) : [];
      return codes.length > 0 ? codes.map(categoryName).join(', ') : 'none';
    }
    if (field.key === WAIVERS_PATH) {
      const n = asWaiverRows(value).length;
      return n === 0 ? 'no waivers' : `${n} ${n === 1 ? 'waiver' : 'waivers'}`;
    }
    if (field.key === DEFINITIONS_PATH) {
      const n = asDefinitions(value).length;
      return n === 0 ? 'none' : `${n} ${n === 1 ? 'custom field' : 'custom fields'}`;
    }
    return undefined;
  };

  // A list whose counts did not move says what moved in it, not "3 fields → 3 fields" (its
  // custom rows named by the stored definitions — a definition's relabel is its own change).
  const storedDefs = asDefinitions(stored[DEFINITIONS_PATH]);
  const describeChange = (field: RecordField, _rec: RecordKey, from: unknown, to: unknown): string | undefined =>
    (LIST_PATHS as readonly string[]).includes(field.key) ? (fieldListSummary(from, to, storedDefs) ?? undefined) : undefined;

  // A scope's fields, its questions and their switch move together (the questions are
  // entries of the field list); with "same fields for party members" on, the party's
  // follow the lead booker's.
  const cascadeTargets = (r: RecordKey, path: string, state: DraftState) => {
    if (r !== rec || !LINKED.has(path)) return [];
    const lead = LEAD_TRIO.includes(path);
    const applySame = state.entries[rec]?.[APPLY_SAME];
    const partyFollows = path === APPLY_SAME || (lead && applySame?.to === true);
    const linked = [...(lead ? LEAD_TRIO : []), ...(!lead || partyFollows ? PARTY_TRIO : [])];
    return linked.filter((p) => p !== path && state.entries[rec]?.[p]).map((p) => ({ rec, path: p }));
  };

  const changeWarnings = (r: RecordKey, path: string, to: unknown): string[] => {
    if (r !== rec) return [];
    if (path === COLLECT_PARTY_PATH && to === false) return [PARTY_NOT_COLLECTED];
    if ((LIST_PATHS as readonly string[]).includes(path)) {
      // A standard field's label is the operator's (a custom field's is its definition's).
      const standard = (v: unknown) => splitFields(v).regular.filter((f) => !isCustomRow(f));
      return sharedFieldWarnings(standard(to), standard(stored[path]), operatorName);
    }
    if (path === DEFINITIONS_PATH) {
      const was = new Map(asDefinitions(stored[DEFINITIONS_PATH]).map((d) => [d.id, d]));
      const out: string[] = [];
      for (const d of asDefinitions(to)) {
        const old = was.get(d.id);
        const n = baseline.definitions.get(d.id)?.used_by.tours ?? 0;
        if (!old || n === 0) continue;
        if (old.name !== d.name) out.push(`“${old.name}” is asked by ${tours(n)} — renaming it changes it on all of them`);
        if (old.type !== d.type) out.push(`“${d.name}” is asked by ${tours(n)} — it becomes ${typeLabel(d.type)} on all of them`);
      }
      return out;
    }
    return [];
  };

  return {
    fields: OPERATOR_PRECHECK_FIELDS,
    options: [],
    actions: [],
    effects: [],
    timezone: null,
    recordNoun: 'pre-check-in settings',
    managedBy: null,
    verdictOf: () => null,
    editabilityOf: () =>
      canEdit ? { editable: true } : { editable: false, reason: `Needs the ${OPERATOR_MANAGE_PERM} permission` },
    groupOf: () => ({ id: 'precheck', title: 'Pre-check-in settings' }),
    cascadeTargets,
    changeWarnings,
    recordOrder: [rec],
    formatValue,
    describeChange,
    confirmFollowUps: CONFIRM_FOLLOW_UPS,
    descriptionMinLength: REASON_MIN,
    descriptionMaxLength: REASON_MAX,
  };
}

export interface OperatorPrecheckConfigHookInput extends OperatorPrecheckConfigInput {
  /** The tab's widgets (`precheck_fields`, …) — the shared pieces over the GET's options. */
  widgets: Record<string, RecordWidget>;
}

/** The tab's `RecordEditorConfig` — stable while its inputs are. */
export function useOperatorPrecheckEditorConfig(input: OperatorPrecheckConfigHookInput): RecordEditorConfig {
  const { baseline, canEdit, operatorName, widgets } = input;
  // The save reads the baseline of the latest GET at save time.
  const baselineRef = useRef(baseline);
  baselineRef.current = baseline;
  const topId = baseline.topId;
  return useMemo<RecordEditorConfig>(
    () => ({
      ...operatorPrecheckConfigParts({ baseline, canEdit, operatorName }),
      widgets,
      usePreview: (body) => useOperatorLocalPlan(body, baseline),
      useApply: ({ onSaved }) => useOperatorPrecheckApply({ topId, baselineRef, onSaved }),
    }),
    [baseline, canEdit, operatorName, widgets, topId],
  );
}
